import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import {
  BlacklistedVisitor,
  DOC_TYPE_ID,
  TABLE_IDS,
} from "../../../constants/common";
import { ArrowUpDown, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";
import RemoveVisitorFromBlacklistingModal from "../../modal/RemoveVisitorFromBlacklistingModal";
import { formattedDateTimeWithoutSec } from "../../../lib/functions";

interface SearchBlacklistVisitorsTableProps {
  allVisitors: never[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const SearchBlacklistVisitorsTable = ({
  allVisitors,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: SearchBlacklistVisitorsTableProps) => {
  const { t, i18n } = useTranslation();

  const [
    isRemoveVisitorFromBlacklistingModalOpen,
    setIsRemoveVisitorFromBlacklistingModalOpen,
  ] = useState(false);

  const [selectedBlacklistedId, setSelectedBlacklistedId] = useState(0);

  const handleRemoveVisitorFromBlacklisting = useCallback(
    async (id: number) => {
      setIsRemoveVisitorFromBlacklistingModalOpen(true);
      setSelectedBlacklistedId(id);
    },
    []
  );

  const columns: ColumnDef<BlacklistedVisitor>[] = useMemo(
    () => [
      // {
      //   accessorKey: "blacklistType_EN",
      //   header: ({ column }) => (
      //     <Button
      //       className="flex w-full justify-between text-md"
      //       variant="ghost"
      //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      //     >
      //       {t("blacklistType")}
      //       <ArrowUpDown className="ml-2 h-4 w-4" />
      //     </Button>
      //   ),
      //   cell: ({ row }) => {
      //     return (
      //       <p>
      //         {i18n.language === "en"
      //           ? row.original.blacklistType_EN
      //           : row.original.blacklistType_AR}
      //       </p>
      //     );
      //   },
      // },
      {
        accessorKey: "idType",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("idType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.idType) return;
          return (
            <span>
              {row.original.idType === DOC_TYPE_ID.NATIONAL_ID
                ? t("nationalID")
                : row.original.idType === DOC_TYPE_ID.PASSPORT
                ? t("passport")
                : row.original.idType === DOC_TYPE_ID.IQAMA
                ? t("iqama")
                : row.original.idType === DOC_TYPE_ID.VISA
                ? t("visa")
                : ""}
            </span>
          );
        },
      },
      {
        accessorKey: "s_EmiratesID, s_PassportNumber, s_VisaNo, s_Iqama",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("idNumber")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span className="capitalize">
              {row.original.idType
                ? row.original.s_EmiratesID ||
                  row.original.s_PassportNumber ||
                  row.original.s_VisaNo ||
                  row.original.s_Iqama
                : ""}
            </span>
          );
        },
      },
      // {
      //   accessorKey: "s_CompanyName",
      //   header: ({ column }) => (
      //     <Button
      //       className="flex w-full justify-between text-md"
      //       variant="ghost"
      //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      //     >
      //       {t("companyName")}
      //       <ArrowUpDown className="ml-2 h-4 w-4" />
      //     </Button>
      //   ),
      // },
      // {
      //   accessorKey: "nationality_EN",
      //   header: ({ column }) => (
      //     <Button
      //       className="flex w-full justify-between text-md"
      //       variant="ghost"
      //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      //     >
      //       {t("nationality")}
      //       <ArrowUpDown className="ml-2 h-4 w-4" />
      //     </Button>
      //   ),
      //   cell: ({ row }) => {
      //     return (
      //       <span>
      //         {i18n.language === "en"
      //           ? row.original.nationality_EN
      //           : row.original.nationality_AR}
      //       </span>
      //     );
      //   },
      // },
      {
        accessorKey: "s_Reason",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reason")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "blacklistedBy",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("blacklistedBy")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "blacklistedAt",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("blacklistedDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {formattedDateTimeWithoutSec(row.original.blacklistedAt)}
            </span>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() =>
                handleRemoveVisitorFromBlacklisting(row.original.n_ID)
              }
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleRemoveVisitorFromBlacklisting, i18n.language, t]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allVisitors}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        tableId={TABLE_IDS.BLACKLIST_VISITORS}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <RemoveVisitorFromBlacklistingModal
        blacklistedId={selectedBlacklistedId}
        isOpen={isRemoveVisitorFromBlacklistingModalOpen}
        onOpenChange={() => setIsRemoveVisitorFromBlacklistingModalOpen(false)}
      />
    </>
  );
};

export default React.memo(SearchBlacklistVisitorsTable);
